import { useEffect, useRef, useState } from 'react';
import {
  cancelRecording,
  startRecording,
  stopRecording,
  type Recorder,
} from '../lib/audio';
import { enqueue as enqueueOutbox, enqueueRecording, finalizeOutbox, onDelivery, abandonOutbox } from '../lib/outbox';
import { useTalkingPublisher } from '../lib/talking';
import { findCrewByToken, parseVoiceCommand, routeMessage, type VoiceCommand } from '../shared/routing';
import { findPlaceByName, nearbyPlace, savePlace, type Place } from '../shared/places';
import { setPresenceStatus } from '../lib/location';
import { startRollCall } from '../lib/rollcall';
import { transcribeAudio, whisperReady } from '../lib/whisper';
import type { CrewMember } from '../state/session';
import { colors } from '../theme';

type LatLng = { lat: number; lng: number };

type LocRow = { lat: number; lng: number; updated_at: string };

type Props = {
  senderId: string;
  groupId: string;
  crew: CrewMember[];
  places: Place[];
  selfLocation: LatLng | null;
  otherLocations: Record<string, LocRow>;
  onCommandResult: (toast: string) => void;
  disabled?: boolean;
  replyToId?: string | null;
  onReplySent?: () => void;
};

type CommandResult = {
  toast: string;
  broadcast?: { transcript: string; recipientId: string | null; urgency: 'urgent' | null };
};

function formatDistance(m: number): string {
  if (m < 100) return `${Math.round(m)}m`;
  if (m < 1000) return `${Math.round(m / 10) * 10}m`;
  return `${(m / 1000).toFixed(1)}km`;
}

type State = 'idle' | 'recording' | 'enqueuing';

// Tap-and-hold PTT for web. No transcription in this slice — that lands with
// whisper-tiny in Day 4. Until then, transcript is null and routing falls back
// to "broadcast, no recipient, no urgency" via an empty routeMessage pass.
// The outbox two-phase flow is still used so upload is parallel (even with a
// trivial transcription pass), matching the native architecture.
export function PTTButton({
  senderId,
  groupId,
  crew,
  places,
  selfLocation,
  otherLocations,
  onCommandResult,
  disabled,
  replyToId,
  onReplySent,
}: Props) {
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<Recorder | null>(null);
  const pressingRef = useRef(false);
  const awaitingDeliveryIdRef = useRef<string | null>(null);
  const [flashCaptured, setFlashCaptured] = useState(false);
  const [flashDelivered, setFlashDelivered] = useState(false);
  const talking = useTalkingPublisher(groupId, senderId);

  // Delivered flash/haptic (web vibrate is unreliable — we use a visual pulse
  // instead). Scoped to the id this button last released so a retry from a
  // prior session doesn't ghost-flash.
  useEffect(() => {
    return onDelivery(({ id }) => {
      if (awaitingDeliveryIdRef.current !== id) return;
      awaitingDeliveryIdRef.current = null;
      setFlashDelivered(true);
      setTimeout(() => setFlashDelivered(false), 1000);
      try {
        navigator.vibrate?.(20);
      } catch {}
    });
  }, []);

  async function onPressIn() {
    if (disabled || state !== 'idle') return;
    pressingRef.current = true;
    setError(null);
    try {
      // Acquire the mic in a single getUserMedia call. A separate permission
      // preflight (acquire-then-stop) races with this acquisition on iOS
      // Safari and throws AbortError: "The operation was aborted."
      const rec = await startRecording();
      if (!pressingRef.current) {
        cancelRecording(rec);
        return;
      }
      recRef.current = rec;
      setState('recording');
      talking.signal(true);
    } catch (e: any) {
      pressingRef.current = false;
      const name = e?.name as string | undefined;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setError('Mic permission denied. Enable it in site settings and try again.');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        setError('No microphone available.');
      } else {
        setError(e?.message ?? String(e));
      }
      setState('idle');
    }
  }

  async function onPressOut() {
    if (!pressingRef.current) return;
    pressingRef.current = false;
    const rec = recRef.current;
    recRef.current = null;
    if (!rec || state !== 'recording') {
      setState('idle');
      return;
    }
    talking.signal(false);
    setState('enqueuing');
    setFlashCaptured(true);
    setTimeout(() => setFlashCaptured(false), 350);

    let blob: Blob;
    let mimeType: string;
    let durationMs: number;
    let outboxId: string;
    let baseTimings: {
      recorded_at: number;
      record_ms: number;
      transcribe_ms: number | null;
      upload_ms: number | null;
      insert_ms: number | null;
      total_ms: number | null;
    };
    const inReplyMode = !!replyToId;
    const capturedReplyToId = replyToId;

    try {
      const result = await stopRecording(rec);
      blob = result.blob;
      mimeType = result.mimeType;
      durationMs = result.durationMs;
      if (durationMs < 400) {
        setState('idle');
        return;
      }
      const recordedAt = Date.now() - durationMs;
      baseTimings = {
        recorded_at: recordedAt,
        record_ms: durationMs,
        transcribe_ms: null,
        upload_ms: null,
        insert_ms: null,
        total_ms: null,
      };

      outboxId = await enqueueRecording({
        sender_id: senderId,
        group_id: groupId,
        blob,
        mime_type: mimeType,
        duration_ms: durationMs,
        timings: baseTimings,
      });
      awaitingDeliveryIdRef.current = outboxId;
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setState('idle');
      return;
    }

    // Recording is safely in the outbox — release the button and let
    // transcribe + finalize finish in the background. The outbox drain will
    // retry upload/insert on its own if anything fails here.
    setState('idle');
    if (inReplyMode) onReplySent?.();

    (async () => {
      let transcript: string | null = null;
      let transcribeMs: number | null = null;
      if (whisperReady()) {
        const t0 = Date.now();
        try {
          transcript = await transcribeAudio(blob);
        } catch (e) {
          console.warn('transcribe failed', e);
        }
        transcribeMs = Date.now() - t0;
      }

      if (!inReplyMode) {
        const cmd = parseVoiceCommand(transcript);
        if (cmd) {
          awaitingDeliveryIdRef.current = null;
          try {
            await abandonOutbox(outboxId);
            const handled = await executeCommand(cmd);
            onCommandResult(handled.toast);
            if (handled.broadcast) {
              const newId = await enqueueOutbox({
                sender_id: senderId,
                group_id: groupId,
                recipient_id: handled.broadcast.recipientId,
                blob,
                mime_type: mimeType,
                transcript: handled.broadcast.transcript,
                duration_ms: durationMs,
                urgency: handled.broadcast.urgency,
                timings: { ...baseTimings, transcribe_ms: transcribeMs },
              });
              awaitingDeliveryIdRef.current = newId;
            }
          } catch (e) {
            console.warn('command path failed', e);
          }
          return;
        }
      }

      const routed = routeMessage(transcript, crew, senderId);
      const recipientId = inReplyMode ? capturedReplyToId! : routed.recipientId;
      try {
        await finalizeOutbox(outboxId, {
          recipient_id: recipientId,
          transcript: transcript ? routed.displayTranscript : null,
          urgency: routed.urgency,
          transcribe_ms: transcribeMs,
        });
      } catch (e) {
        console.warn('finalize failed', e);
      }
    })().catch((e) => console.warn('background send failed', e));
  }

  async function executeCommand(cmd: VoiceCommand): Promise<CommandResult> {
    if (cmd.kind === 'mark_place') {
      if (!selfLocation) return { toast: 'Need a GPS fix before marking a place.' };
      try {
        await savePlace(groupId, senderId, cmd.name, selfLocation.lat, selfLocation.lng);
        return { toast: `Marked this spot as "${cmd.name}".` };
      } catch (e: any) {
        return { toast: `Couldn't save place: ${e?.message ?? String(e)}` };
      }
    }
    if (cmd.kind === 'roll_call') {
      try {
        await startRollCall(groupId, senderId);
        return { toast: 'Roll call started. Waiting on responses.' };
      } catch (e: any) {
        return { toast: `Couldn't start roll call: ${e?.message ?? String(e)}` };
      }
    }
    if (cmd.kind === 'set_presence') {
      try {
        await setPresenceStatus(senderId, cmd.status);
        return {
          toast:
            cmd.status === 'offline'
              ? "You're marked offline. Say \"back online\" to clear it."
              : "You're back online.",
        };
      } catch (e: any) {
        return { toast: `Couldn't update status: ${e?.message ?? String(e)}` };
      }
    }
    if (cmd.kind === 'who_closest') {
      return { toast: whoClosest(cmd.target) };
    }
    if (cmd.kind === 'meet_at') {
      const place = findPlaceByName(places, cmd.placeName);
      if (!place) return { toast: `No saved place called "${cmd.placeName}". Mark it first.` };
      return {
        toast: `Telling the group to meet at ${place.name}.`,
        broadcast: { transcript: `Meet at ${place.name}.`, urgency: null, recipientId: null },
      };
    }
    if (cmd.kind === 'where_is') {
      const target = cmd.target;
      const person = findCrewByToken(crew, target);
      if (person) {
        const loc = otherLocations[person.id];
        if (!loc) return { toast: `${person.display_name} has no location yet.` };
        const minsAgo = Math.max(
          0,
          Math.round((Date.now() - new Date(loc.updated_at).getTime()) / 60000)
        );
        const nearest = nearbyPlace(places, loc.lat, loc.lng);
        const where = nearest ? `near ${nearest.name}` : 'on the map';
        return { toast: `${person.display_name} is ${where} (${minsAgo}m ago).` };
      }
      const place = findPlaceByName(places, target);
      if (place) return { toast: `"${place.name}" is saved on the map.` };
      return { toast: `No match for "${target}".` };
    }
    return { toast: '' };
  }

  function whoClosest(targetName: string | null): string {
    let lat: number;
    let lng: number;
    let label: string;
    if (targetName === null) {
      if (!selfLocation) return 'Need a GPS fix before computing distances.';
      lat = selfLocation.lat;
      lng = selfLocation.lng;
      label = 'you';
    } else {
      const place = findPlaceByName(places, targetName);
      if (!place) return `No saved place called "${targetName}".`;
      lat = place.lat;
      lng = place.lng;
      label = place.name;
    }
    const ranked: { name: string; distM: number }[] = [];
    const FIFTEEN_MIN = 15 * 60 * 1000;
    for (const c of crew) {
      if (c.id === senderId) continue;
      const loc = otherLocations[c.id];
      if (!loc) continue;
      if (Date.now() - new Date(loc.updated_at).getTime() > FIFTEEN_MIN) continue;
      const dx = (loc.lat - lat) * 111_139;
      const dy = (loc.lng - lng) * 111_139 * Math.cos((lat * Math.PI) / 180);
      ranked.push({ name: c.display_name, distM: Math.hypot(dx, dy) });
    }
    if (ranked.length === 0) return `No recent locations to compare against ${label}.`;
    ranked.sort((a, b) => a.distM - b.distM);
    const top = ranked
      .slice(0, 3)
      .map((r) => `${r.name} ${formatDistance(r.distM)}`)
      .join(', ');
    return `Closest to ${label}: ${top}.`;
  }

  const label =
    state === 'recording'
      ? 'Release to send'
      : state === 'enqueuing'
      ? 'Queuing…'
      : 'Hold to talk';

  return (
    <div className="ptt-wrap">
      {error && <div className="ptt-error">{error}</div>}
      <button
        className={[
          'ptt-btn',
          state === 'recording' ? 'ptt-recording' : '',
          state === 'enqueuing' ? 'ptt-enqueuing' : '',
          flashCaptured ? 'ptt-captured' : '',
          flashDelivered ? 'ptt-delivered' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onPointerDown={(e) => {
          e.preventDefault();
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          onPressIn();
        }}
        onPointerUp={() => onPressOut()}
        onPointerCancel={() => onPressOut()}
        onPointerLeave={() => {
          // Don't drop while actively recording — iOS fires spurious leaves.
          // But if we're idle and pointer left, ensure no ghost-press.
          if (!pressingRef.current) return;
        }}
        onContextMenu={(e) => e.preventDefault()}
        disabled={disabled || state === 'enqueuing'}
      >
        <svg
          className="ptt-mic"
          viewBox="0 0 24 24"
          width="36"
          height="36"
          aria-hidden="true"
        >
          <path
            fill="currentColor"
            d="M12 14.5a3.5 3.5 0 0 0 3.5-3.5V6a3.5 3.5 0 1 0-7 0v5a3.5 3.5 0 0 0 3.5 3.5zM19 11a1 1 0 1 0-2 0 5 5 0 0 1-10 0 1 1 0 1 0-2 0 7 7 0 0 0 6 6.92V21a1 1 0 1 0 2 0v-3.08A7 7 0 0 0 19 11z"
          />
        </svg>
      </button>
      <div className="ptt-caption">{label}</div>
      <style>{`
        .ptt-wrap {
          position: absolute;
          bottom: calc(env(safe-area-inset-bottom) + 28px);
          left: 0;
          right: 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          z-index: 1500;
          pointer-events: none;
        }
        .ptt-wrap > * { pointer-events: auto; }
        .ptt-error {
          background: ${colors.danger};
          color: ${colors.bg};
          padding: 8px 14px;
          border-radius: 999px;
          font-size: 13px;
          text-align: center;
          max-width: calc(100% - 24px);
        }
        .ptt-btn {
          width: 104px;
          height: 104px;
          padding: 0;
          border: none;
          border-radius: 50%;
          background: radial-gradient(
            circle at 35% 30%,
            ${colors.accent} 0%,
            ${colors.accent} 55%,
            ${colors.accentDim} 100%
          );
          color: ${colors.bg};
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 80ms ease, box-shadow 120ms ease, filter 120ms ease;
          touch-action: none;
          box-shadow:
            0 8px 20px rgba(0,0,0,0.55),
            0 2px 4px rgba(0,0,0,0.4),
            inset 0 2px 0 rgba(255,255,255,0.25),
            inset 0 -3px 0 rgba(0,0,0,0.25);
          -webkit-tap-highlight-color: transparent;
          cursor: pointer;
        }
        .ptt-btn:active, .ptt-recording {
          transform: scale(0.94);
          filter: brightness(0.92);
          box-shadow:
            0 3px 8px rgba(0,0,0,0.5),
            inset 0 3px 6px rgba(0,0,0,0.4),
            inset 0 -2px 0 rgba(255,255,255,0.12),
            0 0 0 6px ${colors.accent}33;
        }
        .ptt-recording {
          animation: pttPulse 1400ms ease-in-out infinite;
        }
        @keyframes pttPulse {
          0%, 100% {
            box-shadow:
              0 3px 8px rgba(0,0,0,0.5),
              inset 0 3px 6px rgba(0,0,0,0.4),
              0 0 0 6px ${colors.accent}33;
          }
          50% {
            box-shadow:
              0 3px 8px rgba(0,0,0,0.5),
              inset 0 3px 6px rgba(0,0,0,0.4),
              0 0 0 14px ${colors.accent}22;
          }
        }
        .ptt-enqueuing {
          background: radial-gradient(
            circle at 35% 30%,
            ${colors.alt} 0%,
            ${colors.alt} 55%,
            #b8862d 100%
          );
        }
        .ptt-captured { box-shadow: 0 0 0 4px ${colors.alt}, 0 8px 20px rgba(0,0,0,0.55); }
        .ptt-delivered {
          box-shadow: 0 0 0 4px ${colors.alt}, 0 0 24px ${colors.alt}, 0 8px 20px rgba(0,0,0,0.55);
        }
        .ptt-mic {
          display: block;
          pointer-events: none;
        }
        .ptt-btn:disabled { opacity: 0.6; filter: grayscale(0.3); cursor: not-allowed; }
        .ptt-caption {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          color: ${colors.textMuted};
          background: rgba(23,22,28,0.78);
          padding: 4px 12px;
          border-radius: 999px;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          user-select: none;
          -webkit-user-select: none;
        }
      `}</style>
    </div>
  );
}
